import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
} from '@mui/material';
import { Video, Mic } from 'lucide-react';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { InputDetails } from '../../../../../../utils/types/utility_types';
import { IS_IOS } from '../../assigned_assessments';

interface InputSettingsModalProps {
  commenceAssessment: () => void;
  inputDetails: InputDetails;
  setInputDetails: (details: InputDetails) => void;
  setIsWarningModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  setIsModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
}

const defaultCamera: SelectOptions = {
  value: 'default_camera_id',
  label: 'Default Camera',
};
const defaultMic: SelectOptions = {
  value: 'default_mic_id',
  label: 'Default Mic',
};

export const InputSettingsModal: React.FC<InputSettingsModalProps> = ({
  inputDetails,
  setInputDetails,
  setIsWarningModalOpen,
  setIsModalOpen,
}) => {
  const videoEl = useRef<HTMLVideoElement>(null);
  const [cameraList, setCameraList] = useState<SelectOptions[]>([]);
  const [micList, setMicList] = useState<SelectOptions[]>([]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const uniqueMics = new Map<string, SelectOptions>();
      const uniqueCameras = new Map<string, SelectOptions>();

      devices.forEach((device) => {
        // Create a normalized label for the device, removing any numbers in parentheses
        const normalizedLabel = device.label.replace(/ \(\d+\)$/, '').trim();

        if (device.kind === 'audioinput') {
          // Only add if we haven't seen this device label before
          if (!uniqueMics.has(normalizedLabel) && normalizedLabel) {
            uniqueMics.set(normalizedLabel, {
              value: device.deviceId,
              label: normalizedLabel,
            });
          }
        } else if (device.kind === 'videoinput') {
          // Only add if we haven't seen this device label before
          if (!uniqueCameras.has(normalizedLabel) && normalizedLabel) {
            uniqueCameras.set(normalizedLabel, {
              value: device.deviceId,
              label: normalizedLabel,
            });
          }
        }
      });

      // Update state with unique devices
      setMicList(Array.from(uniqueMics.values()));
      setCameraList(Array.from(uniqueCameras.values()));
    });
  }, []);

  useEffect(() => {
    if (!videoEl) {
      return;
    }

    let stream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let scriptProcessor: ScriptProcessorNode | null = null;

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          deviceId: inputDetails.micId,
        },
        video: {
          deviceId: inputDetails.cameraId,
        },
      })
      .then((mediaStream) => {
        stream = mediaStream;
        audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(mediaStream);
        scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);
        scriptProcessor.onaudioprocess = function () {
          const array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          const arraySum = array.reduce((a, value) => a + value, 0);
          const average = arraySum / array.length;
          volumeTracker(average);
        };

        const video = videoEl.current;
        if (video) {
          video.muted = true;
          video.srcObject = mediaStream;
          video.autoplay = true;
        }
      })
      .catch((error) => {
        console.error('❌ InputSettingsModal: Error accessing media devices:', error);
      });

    // Cleanup: Stop all tracks and close audio context when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (scriptProcessor) {
        scriptProcessor.disconnect();
      }

      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().then(() => {});
      }

      if (videoEl.current) {
        videoEl.current.srcObject = null;
      }
    };
  }, [videoEl, inputDetails]);

  const volumeTracker = (volume: number) => {
    const micVolume = document.getElementById('mic-volume');
    if (!micVolume) return;

    const pidList = Array.from(micVolume.children) as HTMLElement[];
    const pidListToColor = Math.round(volume / 10);
    const pidToColor = pidList.slice(0, pidListToColor);

    // Reset all bars to default state
    pidList.forEach((pid) => {
      pid.style.backgroundColor = '#e0e0e0';
    });

    // Color active bars with gradient
    pidToColor.forEach((pid, index) => {
      const intensity = ((index + 1) / pidToColor.length) * 100;
      pid.style.backgroundColor =
        intensity < 50
          ? '#64b5f6'
          : intensity < 75
            ? '#2196f3'
            : '#1976d2';
    });
  };

  return (
    <Box sx={{ maxWidth: 768, mx: 'auto', pb: 2 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant='caption' color='text.secondary'>
          Configure your camera and microphone for the assessment
        </Typography>
      </Box>

      {/* Main Content */}
      <Stack spacing={2.5}>
        {/* Video Preview Section */}
        <Paper
          variant='outlined'
          sx={{
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              maxWidth: 440,
              aspectRatio: '16/9',
              mx: 'auto',
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'common.black',
              boxShadow: 2,
              mb: 1,
            }}
          >
            {IS_IOS ? (
              <Box
                component='video'
                ref={videoEl}
                autoPlay
                playsInline
                muted
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Box
                component='video'
                ref={videoEl}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
          <Typography variant='caption' color='text.secondary'>
            Camera Preview
          </Typography>
        </Paper>

        {/* Settings Section */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {/* Camera Selection */}
          <Paper
            variant='outlined'
            sx={{ p: 2, flex: 1, borderRadius: 2 }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Video size={16} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='subtitle2' fontWeight={600}>
                  Camera Device
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                  Select your preferred webcam
                </Typography>
              </Box>
            </Stack>
            <CustomSelect
              label=''
              name='camera'
              options={cameraList}
              value={defaultCamera}
              onChange={(value: string) =>
                setInputDetails({ ...inputDetails, cameraId: value })
              }
            />
          </Paper>

          {/* Microphone Selection */}
          <Paper
            variant='outlined'
            sx={{ p: 2, flex: 1, borderRadius: 2 }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Mic size={16} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='subtitle2' fontWeight={600}>
                  Microphone Device
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                  Select your preferred microphone
                </Typography>
              </Box>
            </Stack>
            <CustomSelect
              label=''
              name='microphone'
              options={micList}
              value={defaultMic}
              onChange={(value: string) =>
                setInputDetails({ ...inputDetails, micId: value })
              }
            />

            {/* Volume Indicator */}
            <Box sx={{ mt: 2 }}>
              <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5, display: 'block' }}>
                Microphone Level
              </Typography>
              <Box
                id='mic-volume'
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  height: 6,
                }}
              >
                {[...Array(10)].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      flex: 1,
                      borderRadius: '2px',
                      bgcolor: 'action.disabledBackground',
                      transition: 'background-color 0.15s ease',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        </Stack>
      </Stack>

      {/* Action Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            setIsModalOpen(false);
            setIsWarningModalOpen(true);
          }}
          variant='contained'
          color='primary'
          size='large'
        >
          Continue to Assessment
        </Button>
      </Box>
    </Box>
  );
};
